import React, { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { FaBullseye, FaChartLine, FaHandshake, FaShieldHalved } from "react-icons/fa6";
import PageHero from "../components/pageHero";
import ProviderNetworkGraphic from "../images/graphics/provider-network.svg";
import { buildApiUrl } from "../utils/api";

const initialForm = {
  name: "",
  email: "",
  contactNumber: "",
  firmName: "",
  location: "",
  totalArea: "",
  serviceProvided: "",
  serviceableArea: "",
  summary: ""
};

const providerBenefits = [
  { icon: <FaBullseye />, title: "Qualified inbound demand", text: "Receive better-fit leads from buyers who already know the platform." },
  { icon: <FaHandshake />, title: "CS-assisted progression", text: "Your team is not left alone. CS helps align fit and move the deal forward." },
  { icon: <FaChartLine />, title: "Better visibility", text: "Show your coverage, capacity, and services in a cleaner marketplace presentation." },
  { icon: <FaShieldHalved />, title: "Stronger trust signals", text: "A more polished customer-facing site improves confidence for enterprise and SMB buyers alike." }
];

function ProviderMarketplace() {
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ text: "Submitting provider application...", type: "success" });

    try {
      await axios.post(buildApiUrl("/api/providers"), formData);
      setFormData(initialForm);
      setStatus({ text: "Provider application submitted. Our team will be in touch shortly.", type: "success" });
    } catch (error) {
      console.error("Error submitting provider form", error);
      setStatus({ text: "We could not submit the provider form right now. Please try again.", type: "error" });
    }
  };

  return (
    <>
      <Helmet>
        <title>List Your 3PL | 3PL Search</title>
      </Helmet>

      <div className="provider-page">
        <PageHero
          eyebrow="Provider onboarding"
          title="Join the network and present your 3PL with clearer capability signals."
          description="We removed the generic sales-heavy layout and replaced it with a cleaner provider story: what you offer, where you operate, and how your team can support the buyer journey."
          image={ProviderNetworkGraphic}
          imageAlt="Provider network infographic"
          imageLight
        />

        <section className="content-section">
          <div className="container">
            <div className="row g-4">
              {providerBenefits.map((item) => (
                <div className="col-md-6" key={item.title}>
                  <div className="surface-card h-100">
                    <div className="feature-icon">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="content-section muted">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <form className="lead-form-card" onSubmit={handleSubmit}>
                  <div className="section-heading">
                    <span className="eyebrow-badge">Provider application</span>
                    <h2>Tell us about your warehouse operation.</h2>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6"><label>Contact name</label><input name="name" value={formData.name} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Company name</label><input name="firmName" value={formData.firmName} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Phone</label><input name="contactNumber" value={formData.contactNumber} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Primary location</label><input name="location" value={formData.location} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Total area</label><input name="totalArea" value={formData.totalArea} onChange={handleChange} placeholder="e.g. 75,000 sq.ft" required /></div>
                    <div className="col-md-6"><label>Key services provided</label><input name="serviceProvided" value={formData.serviceProvided} onChange={handleChange} placeholder="Pick/pack, B2B, returns, kitting" required /></div>
                    <div className="col-md-6"><label>Serviceable markets</label><input name="serviceableArea" value={formData.serviceableArea} onChange={handleChange} placeholder="US East, UK, EU..." required /></div>
                    <div className="col-12"><label>Operational summary</label><textarea rows="4" name="summary" value={formData.summary} onChange={handleChange} placeholder="Tell us about products handled, fulfillment strengths, systems, SLAs, or unique capabilities." required /></div>
                  </div>
                  <div className="lead-form-actions">
                    <button className="site-primary-cta" type="submit">Submit provider profile</button>
                  </div>
                  {status ? <p className={`form-status ${status.type}`}>{status.text}</p> : null}
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default ProviderMarketplace;
